import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  apiClient as defaultClient,
  type ApiClient,
  type LlmKeyStatus,
  type LlmProvider,
  type LlmProviderDescriptor,
} from '../api/client';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { CapabilityCheck } from '../capability/CapabilityCheck';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { HelpTip } from '../components/HelpTip';
import { FormField } from '../components/FormField';
import { TextInput } from '../components/TextInput';
import { SelectField } from '../components/SelectField';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { toDisplayError, toFieldErrors } from '../api/errorMessages';
import { AuthedBrand } from './AuthedBrand';
import styles from './ProjectScreen.module.css';

/** Static fallback labels used before the registry loads (kept in sync with the
 * server-side PROVIDER_REGISTRY; the loaded providers are authoritative). */
const PROVIDER_LABEL: Record<LlmProvider, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  deepseek: 'DeepSeek',
  custom: 'Custom (OpenAI-compatible)',
};

type LoadState = 'loading' | 'error' | 'ready';

interface FormSummary {
  message: string;
  requestId?: string;
}

/** Human-readable, no-overclaim description of the stored key's state. */
function describeStatus(status: LlmKeyStatus): string {
  if (!status.configured) return 'No key configured.';
  const model = status.model ? ` (${status.model})` : '';
  if (status.status === 'revoked') {
    return `Revoked — the previous ${PROVIDER_LABEL[status.provider]} key ending ••••${status.last4} is no longer usable.`;
  }
  return `Active — ${PROVIDER_LABEL[status.provider]} key ending ••••${status.last4}${model}.`;
}

export interface LlmKeySettingsProps {
  client?: ApiClient;
}

/**
 * BYOK LLM key settings (Stories #32 + #31a). Org-admin only. The plaintext key
 * is write-only: submitted once and NEVER read back — the page only ever shows a
 * masked ••••last4 summary. The provider list, key-page links, and default models
 * come from the server registry (GET …/llm-key/providers) — never hardcoded here.
 * Saving validates the key against the provider BEFORE it is activated (#31a): a
 * bad key surfaces a field/summary error and never disturbs an existing key.
 */
export function LlmKeySettings({ client = defaultClient }: LlmKeySettingsProps) {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const isAdmin = currentOrg?.role === 'org_admin';
  useFocusMain();

  const [phase, setPhase] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [status, setStatus] = useState<LlmKeyStatus>({ configured: false });
  const [providers, setProviders] = useState<LlmProviderDescriptor[]>([]);
  const [provider, setProvider] = useState<LlmProvider>('openai');
  const [model, setModel] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [keyError, setKeyError] = useState<string | undefined>(undefined);
  const [modelError, setModelError] = useState<string | undefined>(undefined);
  const [baseUrlError, setBaseUrlError] = useState<string | undefined>(undefined);
  const [workspaceIdError, setWorkspaceIdError] = useState<string | undefined>(undefined);
  const [summary, setSummary] = useState<FormSummary | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Verify-then-choose-model (Story #196 1b). `verifyPhase` governs the model
  // control; `verifiedModels` holds the live list; `modelFreeText` lets the user
  // opt back into typing a model the list didn't include.
  const [verifyPhase, setVerifyPhase] = useState<'idle' | 'verifying' | 'models-loaded'>('idle');
  const [verifiedModels, setVerifiedModels] = useState<string[]>([]);
  const [modelFreeText, setModelFreeText] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState('');

  const summaryRef = useRef<HTMLDivElement>(null);
  const successRef = useRef<HTMLDivElement>(null);
  const workspaceIdRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (summary) summaryRef.current?.focus();
  }, [summary]);

  const selected = providers.find((p) => p.id === provider);

  const load = useCallback(async () => {
    if (!orgId) return;
    setPhase('loading');
    try {
      const [s, list] = await Promise.all([client.getLlmKey(orgId), client.getLlmProviders(orgId)]);
      setStatus(s);
      setProviders(list);
      const startProvider = s.configured ? s.provider : (list[0]?.id ?? 'openai');
      setProvider(startProvider);
      const desc = list.find((p) => p.id === startProvider);
      setModel(s.configured && s.model ? s.model : (desc?.defaultModels[0] ?? ''));
      setPhase('ready');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    }
  }, [client, orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onProviderChange = (next: LlmProvider) => {
    setProvider(next);
    const desc = providers.find((p) => p.id === next);
    // Default the model to the provider's first suggestion when switching.
    setModel(desc?.defaultModels[0] ?? '');
    setModelError(undefined);
    setBaseUrlError(undefined);
    setWorkspaceId('');
    setWorkspaceIdError(undefined);
    invalidateVerify();
  };

  // Any change to the credentials invalidates a prior verification: a picked
  // model must always correspond to the key that listed it (Story #196 1b).
  const invalidateVerify = () => {
    setVerifyPhase('idle');
    setVerifiedModels([]);
    setModelFreeText(false);
    setVerifyMessage('');
  };

  // Shared field/summary error mapping for Verify and Save. A workspace_id_required
  // 422 lands on the Workspace ID field (with focus) — never a misleading banner.
  const applyKeyErrors = (err: unknown) => {
    const fields = toFieldErrors(err);
    if (fields.apiKey || fields.model || fields.baseUrl || fields.workspaceId) {
      if (fields.apiKey) setKeyError(fields.apiKey);
      if (fields.model) setModelError(fields.model);
      if (fields.baseUrl) setBaseUrlError(fields.baseUrl);
      if (fields.workspaceId) {
        setWorkspaceIdError(fields.workspaceId);
        requestAnimationFrame(() => workspaceIdRef.current?.focus());
      }
      setSummary({ message: 'Please fix the errors below and try again.' });
    } else {
      const display = toDisplayError(err);
      setSummary({
        message: display.message,
        ...(display.requestId ? { requestId: display.requestId } : {}),
      });
    }
  };

  // Optional convenience step: prove the key and list the models it can use,
  // without persisting anything. Save remains the authoritative activation path.
  const verify = async () => {
    if (busy || verifyPhase === 'verifying' || !orgId) return;
    if (apiKey.trim().length < 8) {
      setKeyError('Enter the API key (at least 8 characters).');
      setSuccess(null);
      setSummary({ message: 'Please fix the errors below and try again.' });
      return;
    }
    setKeyError(undefined);
    if (selected?.requiresBaseUrl && baseUrl.trim().length === 0) {
      setBaseUrlError('Enter the base URL for your custom endpoint.');
      setSuccess(null);
      setSummary({ message: 'Please fix the errors below and try again.' });
      return;
    }
    setBaseUrlError(undefined);
    setSummary(null);
    setSuccess(null);
    setWorkspaceIdError(undefined);
    setVerifyPhase('verifying');
    setVerifyMessage('Verifying key…');
    try {
      const { models } = await client.listLlmKeyModels(orgId, {
        provider,
        apiKey: apiKey.trim(),
        ...(selected?.requiresBaseUrl ? { baseUrl: baseUrl.trim() } : {}),
        ...(provider === 'anthropic' && workspaceId.trim()
          ? { workspaceId: workspaceId.trim() }
          : {}),
      });
      setVerifiedModels(models);
      setModelFreeText(false);
      const [firstModel] = models;
      if (firstModel !== undefined) setModel(firstModel);
      setVerifyPhase('models-loaded');
      setVerifyMessage(
        `Key verified — ${models.length} model${models.length === 1 ? '' : 's'} available.`,
      );
    } catch (err) {
      setVerifyPhase('idle');
      setVerifyMessage('');
      applyKeyErrors(err);
    }
  };

  const isActive = status.configured && status.status === 'active';
  const saveLabel = isActive ? 'Rotate key' : 'Save key';

  // The capability check runs against the SAVED active model (the backend reads
  // the persisted key's model). If the on-screen model picker has been changed
  // without saving, running the check would return a verdict for the OLD saved
  // model — fast and stale (#432). Block the check until the change is saved.
  const savedModel = status.configured ? status.model : null;
  const capabilityBlockedReason =
    isActive && savedModel && model !== savedModel
      ? 'Save your model change before checking capability — the check runs against your saved model.'
      : null;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy || !orgId) return;

    let hasError = false;
    if (apiKey.trim().length < 8) {
      setKeyError('Enter the API key (at least 8 characters).');
      hasError = true;
    } else {
      setKeyError(undefined);
    }
    if (model.trim().length === 0) {
      setModelError('Enter the model your key should use.');
      hasError = true;
    } else {
      setModelError(undefined);
    }
    if (selected?.requiresBaseUrl && baseUrl.trim().length === 0) {
      setBaseUrlError('Enter the base URL for your custom endpoint.');
      hasError = true;
    } else {
      setBaseUrlError(undefined);
    }
    if (hasError) {
      setSuccess(null);
      setSummary({ message: 'Please fix the errors below and try again.' });
      return;
    }

    setSummary(null);
    setSuccess(null);
    setBusy(true);
    try {
      const next = await client.setLlmKey(orgId, {
        provider,
        apiKey: apiKey.trim(),
        model: model.trim(),
        ...(selected?.requiresBaseUrl ? { baseUrl: baseUrl.trim() } : {}),
        ...(provider === 'anthropic' && workspaceId.trim()
          ? { workspaceId: workspaceId.trim() }
          : {}),
      });
      setStatus(next);
      setApiKey(''); // never retain the plaintext in memory after submit
      invalidateVerify();
      setSuccess(isActive ? 'Key validated and rotated.' : 'Key validated and saved.');
      requestAnimationFrame(() => successRef.current?.focus());
    } catch (err) {
      applyKeyErrors(err);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (busy || !orgId) return;
    setSummary(null);
    setSuccess(null);
    setBusy(true);
    try {
      await client.revokeLlmKey(orgId);
      setStatus(await client.getLlmKey(orgId));
      setSuccess('Key revoked.');
      requestAnimationFrame(() => successRef.current?.focus());
    } catch (err) {
      const display = toDisplayError(err);
      setSummary({
        message: display.message,
        ...(display.requestId ? { requestId: display.requestId } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <h1 className={styles.heading}>LLM key (BYOK)</h1>
        <p className={styles.subheading}>
          Connect your own model provider key. It is encrypted at rest and only ever shown masked.{' '}
          <HelpTip term="byokKey" />
        </p>

        {phase === 'loading' ? (
          <div className={styles.loading}>
            <Spinner />
            <span>Loading key status…</span>
          </div>
        ) : null}

        {phase === 'error' ? (
          <div className={styles.loadError}>
            <InlineAlert severity="error">{loadMessage}</InlineAlert>
            <Link to="/" className={styles.backLink}>
              Back to home
            </Link>
          </div>
        ) : null}

        {phase === 'ready' ? (
          <>
            <div className={styles.summary}>
              <InlineAlert
                ref={summaryRef}
                severity="error"
                {...(summary?.requestId ? { requestId: summary.requestId } : {})}
              >
                {summary?.message}
              </InlineAlert>
            </div>

            <InlineAlert ref={successRef} severity="success">
              {success}
            </InlineAlert>

            <p aria-live="polite" data-testid="key-status">
              {describeStatus(status)}
            </p>

            {isAdmin ? (
              <form onSubmit={submit} noValidate>
                <SelectField
                  label="Provider"
                  id="llm-provider"
                  value={provider}
                  onChange={(v) => onProviderChange(v as LlmProvider)}
                  options={providers.map((p) => ({ value: p.id, label: p.label }))}
                />

                {selected ? (
                  <p className={styles.subheading}>
                    <a href={selected.keyPageUrl} target="_blank" rel="noreferrer noopener">
                      Get a {selected.label} API key
                    </a>{' '}
                    <span aria-hidden="true">↗</span>
                  </p>
                ) : null}

                {provider === 'anthropic' ? (
                  <FormField
                    label="Workspace ID (optional)"
                    id="llm-workspace-id"
                    helpText="Organization-level Anthropic keys require a Workspace ID; workspace-scoped keys don’t."
                    {...(workspaceIdError ? { error: workspaceIdError } : {})}
                  >
                    {({ controlProps }) => (
                      <TextInput
                        {...controlProps}
                        ref={workspaceIdRef}
                        type="text"
                        name="llm-workspace-id"
                        autoComplete="off"
                        value={workspaceId}
                        onChange={(e) => {
                          setWorkspaceId(e.target.value);
                          setWorkspaceIdError(undefined);
                          invalidateVerify();
                        }}
                      />
                    )}
                  </FormField>
                ) : null}

                {selected?.requiresBaseUrl ? (
                  <FormField
                    label="Base URL"
                    id="llm-base-url"
                    helpText="Your OpenAI-compatible endpoint, e.g. https://llm.example.com/v1."
                    {...(baseUrlError ? { error: baseUrlError } : {})}
                  >
                    {({ controlProps }) => (
                      <TextInput
                        {...controlProps}
                        type="text"
                        inputMode="url"
                        name="llm-base-url"
                        autoComplete="off"
                        value={baseUrl}
                        onChange={(e) => {
                          setBaseUrl(e.target.value);
                          invalidateVerify();
                        }}
                      />
                    )}
                  </FormField>
                ) : null}

                <FormField
                  label={isActive ? 'New API key' : 'API key'}
                  id="llm-api-key"
                  helpText="Pasted once and stored encrypted. We never display it again."
                  {...(keyError ? { error: keyError } : {})}
                >
                  {({ controlProps }) => (
                    <TextInput
                      {...controlProps}
                      type="password"
                      name="llm-api-key"
                      autoComplete="off"
                      value={apiKey}
                      onChange={(e) => {
                        setApiKey(e.target.value);
                        invalidateVerify();
                      }}
                    />
                  )}
                </FormField>

                <div className={styles.verifyRow}>
                  <Button
                    type="button"
                    variant="secondary"
                    busy={verifyPhase === 'verifying' || busy}
                    onClick={verify}
                  >
                    {verifyPhase === 'verifying' ? 'Verifying…' : 'Verify'}
                  </Button>
                  <span className={styles.verifyHint}>
                    Optional: check your key and list the models it can use.
                  </span>
                </div>
                <p className={styles.verifyHint} aria-live="polite" data-testid="verify-status">
                  {verifyMessage}
                </p>

                {verifyPhase === 'models-loaded' && !modelFreeText && verifiedModels.length > 0 ? (
                  <>
                    <SelectField
                      label="Model"
                      id="llm-model"
                      value={model}
                      onChange={(v) => setModel(v)}
                      options={verifiedModels.map((m) => ({ value: m, label: m }))}
                      {...(modelError ? { error: modelError } : {})}
                    />
                    <div className={styles.verifyRow}>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setModelFreeText(true)}
                      >
                        Use a different model
                      </Button>
                    </div>
                  </>
                ) : !modelFreeText && selected && selected.defaultModels.length > 0 ? (
                  <>
                    <SelectField
                      label="Model"
                      id="llm-model"
                      value={model}
                      onChange={(v) => setModel(v)}
                      options={(model && !selected.defaultModels.includes(model)
                        ? [model, ...selected.defaultModels]
                        : selected.defaultModels
                      ).map((m) => ({ value: m, label: m }))}
                      {...(modelError ? { error: modelError } : {})}
                    />
                    <div className={styles.verifyRow}>
                      <Button type="button" variant="ghost" onClick={() => setModelFreeText(true)}>
                        Use a different model
                      </Button>
                    </div>
                  </>
                ) : (
                  <FormField
                    label="Model"
                    id="llm-model"
                    helpText={
                      selected && selected.defaultModels.length > 0
                        ? `Suggested: ${selected.defaultModels.join(', ')}. You may enter any model your key supports.`
                        : 'Enter the model your key should use.'
                    }
                    {...(modelError ? { error: modelError } : {})}
                  >
                    {({ controlProps }) => (
                      <TextInput
                        {...controlProps}
                        name="llm-model"
                        autoComplete="off"
                        value={model}
                        onChange={(e) => setModel(e.target.value)}
                      />
                    )}
                  </FormField>
                )}

                <div className={styles.actions}>
                  <Button type="submit" busy={busy}>
                    {busy ? 'Validating…' : saveLabel}
                  </Button>
                  {isActive ? (
                    <Button type="button" variant="danger" busy={busy} onClick={revoke}>
                      Revoke
                    </Button>
                  ) : null}
                </div>
              </form>
            ) : (
              <InlineAlert severity="info">
                Only an organization admin can add or change the LLM key.
              </InlineAlert>
            )}

            {orgId && isActive ? (
              <CapabilityCheck
                client={client}
                orgId={orgId}
                canRun={isAdmin}
                blockedReason={capabilityBlockedReason}
              />
            ) : null}
          </>
        ) : null}
      </Card>
    </AppShell>
  );
}
